/**
 * Busy monthly file (BUSY-VOUCHER R3): the file's layout constants and the
 * plain-text mapping for everything the "Monthly files" section says about it.
 *
 * Wording is the FINAL wording of R3-copy-review.md (reviewer changes + the
 * OWNER EDITS section + OR-77/OR-78). Server state is DECIDED by the backend
 * (`GET /reports/monthly-files`, contract §2.1) — this file only turns that
 * state into words; it never works out a state from the browser clock.
 *
 * Pure: no React, no API calls. Dates are "3 Nov 2026" (the app's
 * `formatAddedDate` style), organisation not clinic.
 */

import { formatAddedDate, formatPeriodLabel } from '../utils/reportArchive';

// ── File layout (one shared source for the card's "What's inside" and the
//    guide page; mirrors the backend builder — OR-18 / OR-10) ───────────────

/** One Busy voucher series per sheet: `prefix` is the voucher-number prefix. */
export const BUSY_SERIES = Object.freeze([
  { prefix: 'OPD', sheet: 'OPD' },
  { prefix: 'OPDR', sheet: 'OPD Refunds' },
  { prefix: 'OTC', sheet: 'OTC' },
  { prefix: 'OTCR', sheet: 'OTC Refunds' },
  { prefix: 'LAB', sheet: 'Lab' },
  { prefix: 'LABR', sheet: 'Lab Refunds' },
  { prefix: 'RAD', sheet: 'Radiology' },
  { prefix: 'RADR', sheet: 'Radiology Refunds' },
  { prefix: 'IPD', sheet: 'IPD' },
  { prefix: 'IPDR', sheet: 'IPD Refunds' },
].map(Object.freeze));

/**
 * The last two sheets are for reading only, never imported (OR-83): Summary
 * (totals by section and payment mode), then Checks. `note` says what each is for.
 */
export const BUSY_SUMMARY_SHEET = 'Summary';
export const BUSY_CHECKS_SHEET = 'Checks';
export const BUSY_READ_ONLY_SHEETS = Object.freeze([
  { sheet: BUSY_SUMMARY_SHEET, note: 'for reading only; do not import' },
  { sheet: BUSY_CHECKS_SHEET, note: 'totals to compare; do not import' },
].map(Object.freeze));

/** All 12 sheets in file order (10 series sheets, then Summary, then Checks). */
export const BUSY_SHEETS = Object.freeze([
  ...BUSY_SERIES.map((series) => series.sheet),
  ...BUSY_READ_ONLY_SHEETS.map((readOnly) => readOnly.sheet),
]);

/** Said by the Monthly files "What's inside" and the guide alike. */
export const BUSY_SERIES_RULE = `Each sheet except ${BUSY_SUMMARY_SHEET} and ${BUSY_CHECKS_SHEET} is one Busy voucher series.`;

/** The 15 columns, identical on every series sheet (not on Summary / Checks). */
export const BUSY_COLUMNS = Object.freeze([
  'Sr',
  'Date',
  'Voucher Type',
  'Voucher No.',
  'Receipt No.',
  'Section',
  'Patient Name',
  'UHID',
  'Doctor Name',
  'Department',
  'Amount',
  'Payment Mode',
  'Against Receipt',
  'Against Voucher',
  'Narration',
]);

// ── Server enums (contract §2.1 / §2.4) ─────────────────────────────────────

/** `months[].state` — server-decided. */
export const MONTH_FILE_STATE = Object.freeze({
  AVAILABLE: 'AVAILABLE',
  UPCOMING: 'UPCOMING',
  DELAYED: 'DELAYED',
  NONE: 'NONE',
});

/** Failure / hold codes (`books.last_error.code`, `months[].last_error_code`). */
export const BOOKS_ERROR_CODE = Object.freeze({
  DB_UNAVAILABLE: 'DB_UNAVAILABLE',
  S3_UNAVAILABLE: 'S3_UNAVAILABLE',
  BUSY_BOOKS_RECONCILE_MISMATCH: 'BUSY_BOOKS_RECONCILE_MISMATCH',
  EXCEPTIONS_OPEN: 'EXCEPTIONS_OPEN',
  INTERNAL_ERROR: 'INTERNAL_ERROR',
  SOURCE_TRUNCATED: 'SOURCE_TRUNCATED',
  CROSS_ORG_ROW_DETECTED: 'CROSS_ORG_ROW_DETECTED',
  EARLIER_MONTH_PENDING: 'EARLIER_MONTH_PENDING',
});

/** Amber "behind" line shows from this many days (1 day behind = normal). */
export const BOOKS_BEHIND_WARN_DAYS = 2;

// ── Dates ──────────────────────────────────────────────────────────────────

/**
 * "2026-11-03" (a date-only IST day from the API) -> "3 Nov 2026". Goes via a
 * noon-IST instant so the shared `formatAddedDate` renders the SAME day —
 * never `new Date('YYYY-MM-DD')` (UTC midnight; the IST/UTC day-boundary trap).
 */
export function formatBusyDate(ymd) {
  if (!ymd) return '';
  return formatAddedDate(`${ymd}T12:00:00+05:30`);
}

/** UTC ISO instant -> "6 Nov, 02:10" in IST (the "Last problem" time). */
export function formatProblemTime(iso) {
  const [day, month] = formatAddedDate(iso).split(' ');
  const time = new Date(iso).toLocaleTimeString('en-GB', {
    timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
  });
  return `${day} ${month}, ${time}`;
}

// ── Failure reasons (inside "Last problem", and the held-row line) ──────────

// The one place a month goes in a reason: " for {Month YYYY}". It is replaced
// by " for October 2026", or dropped when the error carries no month (a
// nightly-posting problem has `last_error.period: null`).
const MONTH_PHRASE = ' for {Month YYYY}';

/**
 * Backend failure code -> plain reason (R3 plan §2.4 + copy review F1–F7). A
 * null-prototype table, so a direct `BOOKS_ERROR_TEXT[code]` lookup of any
 * string (`'constructor'`, `'__proto__'`) is `undefined`, never a prototype
 * member. Prefer `booksErrorText(code, period)`, which resolves the month and
 * the unknown-code fallback.
 */
export const BOOKS_ERROR_TEXT = Object.freeze(Object.assign(Object.create(null), {
  [BOOKS_ERROR_CODE.DB_UNAVAILABLE]: "Records couldn't be read during the nightly run",
  [BOOKS_ERROR_CODE.S3_UNAVAILABLE]: "The file store couldn't be reached",
  [BOOKS_ERROR_CODE.BUSY_BOOKS_RECONCILE_MISMATCH]: `Totals didn't match the Collections board${MONTH_PHRASE}`,
  [BOOKS_ERROR_CODE.EXCEPTIONS_OPEN]: `Some entries${MONTH_PHRASE} couldn't be posted yet`,
  [BOOKS_ERROR_CODE.INTERNAL_ERROR]: 'Something went wrong during the nightly run',
  [BOOKS_ERROR_CODE.SOURCE_TRUNCATED]: 'A month was too large to read; contact ArogyaKhata support',
  [BOOKS_ERROR_CODE.CROSS_ORG_ROW_DETECTED]: 'An entry from another organisation was found',
}));

// EARLIER_MONTH_PENDING is a read-time ROW hold, not a failure, so it is not in
// the table above: "Not ready yet. September 2026's file must be made first."
function heldMonthText(period) {
  const month = period ? `${formatPeriodLabel(period)}'s` : "An earlier month's";
  return `Not ready yet. ${month} file must be made first.`;
}

/**
 * The plain reason for a server code. `period` is "YYYY-MM" or null. An
 * unknown / missing code (a newer backend code, or `null`) reads as the generic
 * INTERNAL_ERROR line — never the raw code.
 */
export function booksErrorText(code, period) {
  if (code === BOOKS_ERROR_CODE.EARLIER_MONTH_PENDING) return heldMonthText(period);
  const text = BOOKS_ERROR_TEXT[code] || BOOKS_ERROR_TEXT[BOOKS_ERROR_CODE.INTERNAL_ERROR];
  return text.replace(MONTH_PHRASE, period ? ` for ${formatPeriodLabel(period)}` : '');
}

// ── Month rows ─────────────────────────────────────────────────────────────

/**
 * A NONE month that is simply earlier than the books' start date (OR-77): the
 * server says NONE for those whenever the books are on. NONE with the books
 * off (or the status unreadable) is a different row ("No Busy file for this
 * month.") and is not a before-start month.
 */
export function isBeforeStartMonth(month, books) {
  return month.state === MONTH_FILE_STATE.NONE && !!books?.enabled && !!books.start_date;
}

/**
 * The one month that is actually failing: tried at least once and not just
 * held behind an older month. Only this row is drawn amber; waiting rows
 * (upcoming, never tried, held, before start) stay neutral grey.
 */
export function isFailingMonth(month) {
  return month.state === MONTH_FILE_STATE.DELAYED && !month.waiting_for && month.attempts > 0;
}

/**
 * The one-line note under a month row, from a `months[]` entry and the
 * response's `books` object (null when the status read failed).
 *  - AVAILABLE: the shipped reason line (the file details come from `file`).
 *  - NONE: before the books' start date (OR-77) / otherwise no file.
 *  - DELAYED: held behind an older month, else tries so far (0 = none yet).
 */
export function monthRowNote(month, books) {
  switch (month.state) {
    case MONTH_FILE_STATE.AVAILABLE:
      return 'Available for download';
    case MONTH_FILE_STATE.UPCOMING:
      return `Made automatically on ${formatBusyDate(month.made_on)}`;
    case MONTH_FILE_STATE.DELAYED:
      if (month.waiting_for) {
        return booksErrorText(BOOKS_ERROR_CODE.EARLIER_MONTH_PENDING, month.waiting_for);
      }
      return month.attempts > 0
        ? `Not ready yet. Retried every night · Attempts: ${month.attempts}`
        : 'Not ready yet. It will be made on the next nightly run.';
    case MONTH_FILE_STATE.NONE:
      return isBeforeStartMonth(month, books)
        ? `No Busy file — before books start (${formatBusyDate(books.start_date)})`
        : 'No Busy file for this month.';
    default:
      return null;
  }
}

/**
 * "from 31 Jul" for the month the books started in when they started part-way
 * through it (OR-76: that month's file begins on the start date). null for any
 * other month, and for a start on the 1st (a whole month).
 */
export function monthFromNote(month, books) {
  const start = books?.start_date;
  if (!start || start.slice(0, 7) !== month.period || start.slice(8, 10) === '01') return null;
  return `from ${formatBusyDate(start).replace(/ \d{4}$/, '')}`;
}

/** "April – June 2026" (one year) or "October 2026 – March 2027" (across a new year). */
export function foldedMonthsLabel(months) {
  const newest = formatPeriodLabel(months[0].period);
  const oldest = formatPeriodLabel(months[months.length - 1].period);
  const [oldestName, oldestYear] = oldest.split(' ');
  return oldestYear === newest.split(' ')[1] ? `${oldestName} – ${newest}` : `${oldest} – ${newest}`;
}

/**
 * Newest-first `months` of one financial year -> display items. A run of two or
 * more before-start months folds into ONE item (a late go-live would otherwise
 * list ten identical rows); everything else is its own item.
 * Items: `{ month }` or `{ folded: [month, ...], label }` (same newest-first order).
 */
export function foldBeforeStartRuns(months, books) {
  const items = [];
  let run = [];
  const flush = () => {
    if (run.length === 1) items.push({ month: run[0] });
    else if (run.length > 1) items.push({ folded: run, label: foldedMonthsLabel(run) });
    run = [];
  };
  months.forEach((month) => {
    if (isBeforeStartMonth(month, books)) {
      run.push(month);
    } else {
      flush();
      items.push({ month });
    }
  });
  flush();
  return items;
}

// ── Download 404 (the file is gone from the file store) ─────────────────────

/** A Download came back "not found" (REPORT_NOT_AVAILABLE). Claims nothing about the list. */
export const FILE_NOT_FOUND_TEXT = "This file couldn't be found.";

/** ...and the list was re-read afterwards: it now shows the real state. */
export const FILE_NOT_FOUND_REFRESHED_TEXT = `${FILE_NOT_FOUND_TEXT} The list has been refreshed.`;

/** ...but the re-read failed: the list is as stale as before. */
export const FILE_NOT_FOUND_RETRY_TEXT = `${FILE_NOT_FOUND_TEXT} Try again later.`;

/** The row's note while that re-read is in flight (neutral: not "available", not "missing"). */
export const FILE_RECHECKING_TEXT = 'Checking the list…';

// ── Status block lines (all take the response's `books` object) ─────────────

/** "Books posted up to 23 Oct 2026." — or the start date before the first post. */
export function booksPostedLine(books) {
  if (books.posted_up_to) return `Books posted up to ${formatBusyDate(books.posted_up_to)}.`;
  if (books.start_date) return `Books start on ${formatBusyDate(books.start_date)}.`;
  return null;
}

/** The amber line, only once the books are `BOOKS_BEHIND_WARN_DAYS`+ behind. */
export function booksBehindLine(books) {
  const days = books.days_behind;
  if (!(days >= BOOKS_BEHIND_WARN_DAYS)) return null;
  return `Books are ${days} days behind. Missing days are added automatically on the next successful nightly run.`;
}

/**
 * "Last problem: {reason} (6 Nov, 02:10). Attempts: 3." — `lastError` is
 * `books.last_error` (current problem only), `attempts` the number of tries so
 * far; "Attempts" is left out when there are none to report. Every reason is a
 * fragment with NO full stop (copy review C3), so the line reads
 * "…support (6 Nov, 02:10)." — the full stop belongs to the line, not the reason.
 */
export function booksLastProblemLine(lastError, attempts) {
  if (!lastError) return null;
  const reason = booksErrorText(lastError.code, lastError.period);
  const tries = attempts > 0 ? ` Attempts: ${attempts}.` : '';
  return `Last problem: ${reason} (${formatProblemTime(lastError.at)}).${tries}`;
}

/** "{n} entries couldn't be posted yet. Contact ArogyaKhata support." (count only). */
export function booksExceptionsLine(count) {
  if (!(count > 0)) return null;
  const entries = count === 1 ? '1 entry' : `${count} entries`;
  return `${entries} couldn't be posted yet. Contact ArogyaKhata support.`;
}

/** Red safety-stop line (`books.cross_org_stop`). */
export const BOOKS_CROSS_ORG_TEXT =
  'Books stopped for safety: an entry from another organisation was found. '
  + 'Contact ArogyaKhata support. Files already made can still be downloaded.';

/** `books_status_error` is set: rows below are still listed and downloadable. */
export const BOOKS_STATUS_UNAVAILABLE_TEXT =
  "Books status couldn't be loaded. Files below can still be downloaded.";

/** `books.enabled` is false (files switched off for this organisation). */
export const BOOKS_OFF_TEXT = 'To start generating monthly files, contact ArogyaKhata support.';

/** Under the Monthly files list and at the foot of the Busy guide. */
export const BUSY_TRADEMARK_NOTE = 'BUSY is a trademark of its owner. ArogyaKhata is not affiliated with BUSY.';

/** `fiscal_years` is empty: nothing to list. */
export const MONTHLY_FILES_EMPTY_TEXT =
  'No monthly files yet. They start once ArogyaKhata support switches on Busy books for your organisation.';

// ── Section header ──────────────────────────────────────────────────────────

/** 1 -> "1st", 2 -> "2nd", 3 -> "3rd", 4 -> "4th", 11 -> "11th", 22 -> "22nd". */
function ordinalDay(n) {
  const teen = n % 100 >= 11 && n % 100 <= 13;
  const suffix = teen ? 'th' : ({ 1: 'st', 2: 'nd', 3: 'rd' }[n % 10] || 'th');
  return `${n}${suffix}`;
}

/**
 * The (i) help sentence. `releaseDay` is the server's `release_day` (the day of
 * the next month the file is made), so the day is never hard-coded here; before
 * the first response arrives the sentence leaves the day out.
 */
export function monthlyFilesHelp(releaseDay) {
  const when = releaseDay ? `on the ${ordinalDay(releaseDay)} of the next month` : 'after each month ends';
  return `One Busy import file per month, made automatically ${when}. Covers the whole organisation: all sections, all doctors.`;
}
